import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiInstanceFetch } from "../../util/api";
import { setToast } from "../../util/toast";
import { currentRole } from "../../Components/navbar/Sidebar";

/// Bolge yoneticisi hedef takibi.
/// Sahip tum ajanslari gorur ve yonetici secebilir; bolge muduru yalnizca
/// kendine bagli ajanslari gorur (sunucu tarafinda da kisitli).
const AgencyTargets = () => {
  const [data, setData] = useState(null);
  const [managers, setManagers] = useState([]);
  const [managerId, setManagerId] = useState("");
  const [month, setMonth] = useState("");
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState([]);
  const [form, setForm] = useState(null); // {name, ownerCode, contact}

  const isOwner = currentRole() === "owner";

  const load = useCallback(() => {
    const q = [];
    if (month) q.push(`month=${month}`);
    if (isOwner && managerId) q.push(`managerId=${managerId}`);
    apiInstanceFetch
      .get(`agency/admin/managerStats${q.length ? "?" + q.join("&") : ""}`)
      .then((res) => {
        if (res?.status) setData(res);
        else setToast("error", res?.message || "Yüklenemedi");
      })
      .catch(() => setToast("error", "Yüklenemedi"));
  }, [month, managerId, isOwner]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isOwner) return;
    apiInstanceFetch
      .get("agency/admin/managerList")
      .then((res) => res?.status && setManagers(res.managers || []))
      .catch(() => {});
  }, [isOwner]);

  // sahip: her Super Admin kac ajans acmis, kaci gecerli
  useEffect(() => {
    if (!isOwner) return;
    apiInstanceFetch
      .get(`agency/admin/managerSummary${month ? `?month=${month}` : ""}`)
      .then((res) => res?.status && setSummary(res.managers || []))
      .catch(() => {});
  }, [isOwner, month, busy]);

  const assign = (agencyId, mid) => {
    setBusy(true);
    apiInstanceFetch
      .post(`agency/admin/${agencyId}/action`, { action: "assignManager", managerId: mid })
      .then((res) => {
        if (res?.status) {
          setToast("success", mid ? "Yöneticiye bağlandı" : "Bağlantı kaldırıldı");
          load();
        } else setToast("error", res?.message || "İşlem başarısız");
      })
      .catch(() => setToast("error", "İşlem başarısız"))
      .finally(() => setBusy(false));
  };

  const createAgency = () => {
    const f = form || {};
    if (!f.name || !f.ownerCode) return setToast("error", "Ajans adı ve sahibinin ID'si gerekli");
    setBusy(true);
    apiInstanceFetch
      .post("agency/admin/create", { name: f.name, ownerCode: f.ownerCode, contact: f.contact || "" })
      .then((res) => {
        if (res?.status) {
          setToast("success", "Ajans açıldı");
          setForm(null);
          load();
        } else setToast("error", res?.message || "Açılamadı");
      })
      .catch(() => setToast("error", "Açılamadı"))
      .finally(() => setBusy(false));
  };

  const T = data?.targets || {};
  const valid = data?.valid || 0;
  const target = data?.target || 0;
  const pct = target > 0 ? Math.min(100, Math.round((valid / target) * 100)) : 0;

  const months = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return out;
  }, []);

  const num = (n) => (Number(n) || 0).toLocaleString("tr-TR");
  const S = styles;

  return (
    <>
      <div className="row py-2">
        <div className="col-12">
          <h4 className="hostTitle">Hedef takibi</h4>
        </div>
      </div>

      <div className="row layout-top-spacing">
        <div className="col-lg-12 col-12 layout-spacing">
          <div className="statbox widget">
            <div className="widget-content widget-content-area" style={S.area}>
              {/* filtreler */}
              <div style={S.toolbar}>
                <div style={S.filters}>
                  <select style={S.select} value={month} onChange={(e) => setMonth(e.target.value)}>
                    {months.map((m, i) => (
                      <option key={m} value={i === 0 ? "" : m}>
                        {m} {i === 0 ? "(bu ay)" : ""}
                      </option>
                    ))}
                  </select>

                  {isOwner && (
                    <select style={S.select} value={managerId} onChange={(e) => setManagerId(e.target.value)}>
                      <option value="">Tüm ajanslar</option>
                      {managers.map((m) => (
                        <option key={m._id} value={m._id}>
                          {m.name} {m.region ? `· ${m.region}` : ""}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" style={{ ...S.btn, ...S.btnGhost }} onClick={load}>
                    Yenile
                  </button>
                  <button
                    type="button"
                    style={{ ...S.btn, ...S.btnPrimary }}
                    onClick={() => setForm(form ? null : { name: "", ownerCode: "", contact: "" })}
                  >
                    {form ? "Vazgeç" : "+ Ajans aç"}
                  </button>
                </div>
              </div>

              {/* ajans acma formu */}
              {form && (
                <div style={S.form}>
                  <div style={S.formTitle}>Yeni ajans</div>
                  <div style={S.formRow}>
                    <input
                      style={S.input}
                      placeholder="Ajans adı"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                    <input
                      style={S.input}
                      placeholder="Sahibinin ID'si (uygulamadaki #numara)"
                      value={form.ownerCode}
                      onChange={(e) => setForm({ ...form, ownerCode: e.target.value })}
                    />
                    <input
                      style={S.input}
                      placeholder="İletişim (isteğe bağlı)"
                      value={form.contact}
                      onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    />
                    <button type="button" style={{ ...S.btn, ...S.btnPrimary }} disabled={busy} onClick={createAgency}>
                      Aç
                    </button>
                  </div>
                  <div style={S.formNote}>
                    Açtığın ajans doğrudan sana yazılır ve hedefine sayılır. Sahibi yayın açabilir hale gelir.
                  </div>
                </div>
              )}

              {/* sahip: Super Admin karnesi */}
              {isOwner && summary.length > 0 && (
                <div style={S.board}>
                  <div style={S.boardTitle}>Super Admin karnesi</div>
                  <div style={S.scroll}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Super Admin</th>
                          <th style={S.th}>Bölge</th>
                          <th style={{ ...S.th, textAlign: "right" }}>Açtığı ajans</th>
                          <th style={{ ...S.th, textAlign: "right" }}>Bu ay geçerli</th>
                          <th style={{ ...S.th, textAlign: "right" }}>Aylık ciro</th>
                          <th style={{ ...S.th, textAlign: "right" }}>Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.map((m) => (
                          <tr key={m._id}>
                            <td style={S.td}>
                              <b>{m.name}</b>
                              {!m.isActive && <span style={{ ...S.badge, ...S.badgeRed, marginLeft: 6 }}>kapalı</span>}
                            </td>
                            <td style={S.td}>{m.region || "—"}</td>
                            <td style={{ ...S.td, textAlign: "right" }}>{m.agencyCount}</td>
                            <td style={{ ...S.td, textAlign: "right" }}>
                              <b style={{ color: m.valid >= m.target ? "#067647" : "#b42318" }}>{m.valid}</b> / {m.target}
                            </td>
                            <td style={{ ...S.td, textAlign: "right" }}>{num(m.monthCoin)}</td>
                            <td style={{ ...S.td, textAlign: "right" }}>
                              <span style={{ ...S.badge, ...(m.valid >= m.target ? S.badgeGreen : S.badgeGrey) }}>
                                {m.valid >= m.target ? "Hedef tamam" : `${Math.max(0, m.target - m.valid)} kaldı`}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* hedef ozeti */}
              <div style={S.summary}>
                <div style={S.sumBlock}>
                  <div style={S.sumLabel}>Bu ay geçerli ajans</div>
                  <div style={S.sumValue}>
                    {valid}
                    <span style={S.sumOf}> / {target}</span>
                  </div>
                </div>
                <div style={S.barWrap}>
                  <div style={S.bar}>
                    <div
                      style={{
                        ...S.barFill,
                        width: `${pct}%`,
                        background: valid >= target ? "#12b76a" : "#4361ee",
                      }}
                    />
                  </div>
                  <div style={S.barNote}>
                    {valid >= target
                      ? "Hedef tamam"
                      : `Hedefe ${Math.max(0, target - valid)} ajans kaldı`}
                  </div>
                </div>
              </div>

              {/* kural hatirlatmasi */}
              <div style={S.rules}>
                <span>
                  <b>Aktif yayıncı:</b> ayda en az {T.activeHostDays} gün yayın + {num(T.activeHostCoin)} coin
                </span>
                <span style={S.dot}>•</span>
                <span>
                  <b>Geçerli ajans:</b> en az {T.validAgencyHosts} aktif yayıncı + {num(T.validAgencyCoin)} coin ciro
                </span>
              </div>

              {/* ajans listesi */}
              {(data?.agencies || []).length === 0 ? (
                <div style={S.empty}>Bu ay için ajans kaydı yok.</div>
              ) : (
                (data?.agencies || []).map((a) => (
                  <div key={a._id} style={S.card}>
                    <div style={S.cardTop}>
                      <div style={{ minWidth: 0, flex: "1 1 240px" }}>
                        <div style={S.nameRow}>
                          <span style={S.name}>{a.name}</span>
                          <span style={S.code}>#{a.code}</span>
                          {a.valid ? (
                            <span style={{ ...S.badge, ...S.badgeGreen }}>Geçerli</span>
                          ) : (
                            <span style={{ ...S.badge, ...S.badgeGrey }}>Geçersiz</span>
                          )}
                          {a.status !== "active" && (
                            <span style={{ ...S.badge, ...S.badgeRed }}>{a.status}</span>
                          )}
                        </div>
                        <div style={S.meta}>
                          <span>
                            Aktif yayıncı{" "}
                            <b style={{ color: a.activeHosts >= T.validAgencyHosts ? "#067647" : "#b42318" }}>
                              {a.activeHosts}
                            </b>{" "}
                            / {a.memberCount}
                          </span>
                          <span style={S.dot}>•</span>
                          <span>
                            Aylık ciro{" "}
                            <b style={{ color: a.monthCoin >= T.validAgencyCoin ? "#067647" : "#b42318" }}>
                              {num(a.monthCoin)}
                            </b>{" "}
                            coin
                          </span>
                        </div>
                      </div>

                      <div style={S.actions}>
                        {isOwner && (
                          <select
                            style={{ ...S.select, minWidth: 170 }}
                            value={a.managerId || ""}
                            disabled={busy}
                            onChange={(e) => assign(a._id, e.target.value)}
                          >
                            <option value="">Yönetici yok</option>
                            {managers.map((m) => (
                              <option key={m._id} value={m._id}>
                                {m.name}
                              </option>
                            ))}
                          </select>
                        )}
                        <button
                          type="button"
                          style={{ ...S.btn, ...S.btnGhost }}
                          onClick={() => setOpen(open === a._id ? null : a._id)}
                        >
                          {open === a._id ? "Gizle" : `Yayıncılar (${a.memberCount})`}
                        </button>
                      </div>
                    </div>

                    {open === a._id && (
                      <div style={S.hostList}>
                        {a.hosts.length === 0 ? (
                          <div style={S.emptySmall}>Bu ajansta aktif yayıncı yok.</div>
                        ) : (
                          a.hosts.map((h) => (
                            <div key={h.hostId} style={S.hostRow}>
                              <span style={{ ...S.hostDot, background: h.active ? "#12b76a" : "#d3d8e3" }} />
                              <span style={S.hostName}>{h.name || "—"}</span>
                              <span style={S.hostStat}>{h.days} gün</span>
                              <span style={S.hostStat}>{num(h.coins)} coin</span>
                              <span style={{ ...S.hostTag, ...(h.active ? S.tagOn : S.tagOff) }}>
                                {h.active ? "Aktif" : "Pasif"}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  area: { padding: 18 },
  toolbar: { display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  filters: { display: "flex", gap: 8, flexWrap: "wrap" },
  select: {
    border: "1px solid #dde2ec",
    borderRadius: 10,
    padding: "9px 12px",
    fontSize: 14,
    background: "#fff",
    color: "#42506b",
    outline: "none",
  },
  summary: {
    display: "flex",
    flexWrap: "wrap",
    gap: 20,
    alignItems: "center",
    background: "#f6f8fc",
    border: "1px solid #eceff5",
    borderRadius: 14,
    padding: "18px 20px",
    marginBottom: 12,
  },
  sumBlock: { minWidth: 160 },
  sumLabel: { fontSize: 12, color: "#8b93a7", fontWeight: 600 },
  sumValue: { fontSize: 30, fontWeight: 800, color: "#1b2334", marginTop: 2, lineHeight: 1 },
  sumOf: { fontSize: 17, color: "#8b93a7", fontWeight: 700 },
  barWrap: { flex: "1 1 240px", minWidth: 200 },
  bar: { height: 10, borderRadius: 99, background: "#e4e9f2", overflow: "hidden" },
  barFill: { height: "100%", borderRadius: 99, transition: "width .3s" },
  barNote: { marginTop: 7, fontSize: 13, color: "#6b7280", fontWeight: 600 },
  rules: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
    fontSize: 12.5,
    color: "#6b7280",
    marginBottom: 16,
    padding: "0 2px",
  },
  card: {
    border: "1px solid #eceff5",
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    background: "#fff",
    boxShadow: "0 1px 3px rgba(20,30,60,.05)",
  },
  cardTop: { display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" },
  nameRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  name: { fontWeight: 700, fontSize: 16, color: "#1b2334" },
  code: { color: "#8b93a7", fontSize: 13 },
  badge: { fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "2px 10px" },
  badgeGreen: { background: "#e3f9ec", color: "#067647" },
  badgeGrey: { background: "#eef1f6", color: "#6b7280" },
  badgeRed: { background: "#ffe3e3", color: "#b42318" },
  meta: { marginTop: 5, fontSize: 13, color: "#6b7280", display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" },
  dot: { color: "#c8cddb" },
  actions: { display: "flex", gap: 8, flexWrap: "wrap", marginLeft: "auto", alignItems: "center" },
  btn: { border: "1px solid transparent", borderRadius: 10, padding: "8px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  btnGhost: { background: "#fff", borderColor: "#dde2ec", color: "#42506b" },
  hostList: { marginTop: 12, paddingTop: 12, borderTop: "1px solid #eef1f6", display: "flex", flexDirection: "column", gap: 2 },
  hostRow: { display: "flex", alignItems: "center", gap: 10, padding: "7px 2px", fontSize: 13.5, flexWrap: "wrap" },
  hostDot: { width: 8, height: 8, borderRadius: 4, flex: "none" },
  hostName: { flex: "1 1 140px", color: "#1b2334", fontWeight: 600, minWidth: 0 },
  hostStat: { color: "#6b7280", minWidth: 90, textAlign: "right" },
  hostTag: { fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "2px 9px", minWidth: 54, textAlign: "center" },
  tagOn: { background: "#e3f9ec", color: "#067647" },
  tagOff: { background: "#f1f3f8", color: "#8b93a7" },
  btnPrimary: { background: "#4361ee", color: "#fff" },
  form: {
    border: "1px solid #dde2ec",
    borderRadius: 14,
    padding: 16,
    marginBottom: 14,
    background: "#f9fbff",
  },
  formTitle: { fontWeight: 700, fontSize: 14.5, color: "#1b2334", marginBottom: 10 },
  formRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  input: {
    flex: "1 1 190px",
    border: "1px solid #dde2ec",
    borderRadius: 10,
    padding: "9px 12px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
  },
  formNote: { fontSize: 12.5, color: "#8b93a7", marginTop: 9 },
  board: {
    border: "1px solid #eceff5",
    borderRadius: 14,
    background: "#fff",
    padding: 16,
    marginBottom: 14,
  },
  boardTitle: { fontWeight: 700, fontSize: 14.5, color: "#1b2334", marginBottom: 10 },
  scroll: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13.5, minWidth: 560 },
  th: {
    textAlign: "left",
    padding: "9px 10px",
    fontSize: 11.5,
    letterSpacing: ".05em",
    textTransform: "uppercase",
    color: "#8b93a7",
    borderBottom: "1px solid #eceff5",
    whiteSpace: "nowrap",
  },
  td: { padding: "10px", color: "#42506b", borderBottom: "1px solid #f4f6fa", whiteSpace: "nowrap" },
  empty: { textAlign: "center", color: "#8b93a7", padding: "48px 0", fontSize: 15 },
  emptySmall: { color: "#8b93a7", fontSize: 13, padding: "8px 2px" },
};

export default AgencyTargets;
